import {
  faAngleRight,
  faBars,
  faClock,
  faFire,
  faFireAlt,
  faLightbulb,
  faList,
  faSearch,
  faTags
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddSearchData,
  addSearchLog,
  getOnSearch,
  getSearchDetail,
  ProductSearch,
  SearchData,
  SearchDetails
} from 'api/globalsearh/_requests';
import Scrollbar from 'components/base/Scrollbar';
import { debounce } from 'lodash';
import { CSSProperties, useMemo, useRef, useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  FormControlProps,
  Dropdown,
  Card
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from 'redux-state/store';

export interface SearchBoxProps extends FormControlProps {
  placeholder?: string;
  style?: CSSProperties;
  container?: boolean;
}

const GlobalSearchBox = ({
  placeholder = 'Search Products, Categories, Brands…',
  style,
  container = false,
  ...rest
}: SearchBoxProps) => {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: searchlogDetail, isLoading: isCategoriesLoading } =
    useQuery<SearchDetails>({
      queryKey: ['searchlog-details'],
      queryFn: () => getSearchDetail()
    });

  const { data: SearchData, isLoading: isProductsTypeLoading } =
    useQuery<SearchData>({
      queryKey: ['searchlog-by-search', input],
      queryFn: () => getOnSearch(input),
      enabled: !!input
    });
  const debouncedSetSearch = useMemo(
    () => debounce((val: string) => setSearch(val), 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    debouncedSetSearch(val);
  };

  const handleInputFocus = () => {
    setIsSearchOpen(true);
    setIsCategoryOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
      setIsCategoryOpen(false);
      setIsSearchOpen(false);
    }
  };

  const handleAddSearchLog = async (customValue?: string) => {
    const value = (customValue || input || '').trim();
    if (!value) return;

    const payload: AddSearchData = {
      userId: user?._id,
      text: value
    };

    try {
      const response = await addSearchLog(payload);
      if (response.status === true) {
        queryClient.invalidateQueries({ queryKey: ['searchlog-details'] });

        if (response.type === 'product') {
          setIsSearchOpen(false);
          navigate(`/products-filter`, {
            state: {
              search: value
            }
          });
        } else if (response.type === 'category') {
          setIsSearchOpen(false);
          navigate(`/products-filter`, {
            state: {
              category: response?.data?.id,
              categoryType: response.data?.categoryType
            }
          });
        } else if (response.type === 'brand') {
          setIsSearchOpen(false);
          navigate(
            `/branddetails/${response?.data?.slug || response?.data?.id}`
          );
        } else {
          navigate(`/products-filter`);
          setIsSearchOpen(false);
        }
      }
    } catch (err) {
      console.error('Search log error:', err);
      navigate(`/products-filter`, { state: { search: value } });
      setIsSearchOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderSearchResults = () =>
    isSearchOpen && (
      <div
        className="position-absolute mt-1 w-100 shadow rounded ecommerce-navbar bg-body-emphasis"
        style={{ zIndex: 999999 }}
      >
        {search ? (
          <>
            <Container className="mt-3 mb-3">
              <>
                {SearchData?.products && (
                  <>
                    <h6 className="mt-4 mb-2 text-muted d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faLightbulb}
                        className="text-warning"
                      />
                      <strong>Suggestions</strong>
                    </h6>

                    <Row className="g-2">
                      {SearchData.products.map((product, index) => (
                        <Col key={index} xs={12}>
                          <div
                            className="border-bottom py-2 px-2 text-dark small cursor-pointer"
                            onClick={() => {
                              setInput(product.name);
                              handleAddSearchLog(product.name);
                              setIsSearchOpen(false);
                            }}
                          >
                            <span className="text-muted">{product.name}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </>

              {SearchData?.categories && (
                <>
                  <h6 className="mt-4 mb-3 text-muted d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faList} className="text-primary" />
                    <strong>Relevant Categories</strong>
                  </h6>

                  <Row className="g-3">
                    {SearchData.categories.map((category, index) => (
                      <Col key={index} xs={12} sm={6} md={4}>
                        <Link
                          to="#"
                          className="text-decoration-none"
                          onClick={e => {
                            e.preventDefault();
                            setInput(category?.name || '');
                            handleAddSearchLog(category.name);
                            setIsSearchOpen(false);
                          }}
                        >
                          <div className="d-flex align-items-center border rounded p-2 bg-body-emphasis">
                            <img
                              src={
                                // category.imageUrl ||
                                'https://cdn.moglix.com/cms/element/Images_2020-07-21_14-35-04_Images_2020-04-30_03-10-15_safety@2x.png'
                              }
                              alt={category.name}
                              className="me-3 rounded"
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                flexShrink: 0
                              }}
                            />
                            <span className="text-muted small fw-semibold">
                              {category.name || ''}
                            </span>
                          </div>
                        </Link>
                      </Col>
                    ))}
                  </Row>
                </>
              )}

              {SearchData?.brands && (
                <>
                  <h6 className="mt-4 mb-3 text-muted d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faTags} className="text-success" />
                    <strong>Explore by Brands</strong>
                  </h6>

                  <Row className="g-3 ">
                    {SearchData.brands.map((brand, index) => (
                      <Col key={index} xs={12} sm={6} md={2}>
                        <Link
                          to={`#`}
                          onClick={e => {
                            e.preventDefault();
                            setInput(brand.name);
                            handleAddSearchLog(brand.name);
                            setIsSearchOpen(false);
                          }}
                          className="text-decoration-none d-block"
                        >
                          <button
                            type="button"
                            className="w-100 border bg-body-emphasis rounded p-2 text-muted fw-semibold small"
                          >
                            {brand.name || ''}
                          </button>
                        </Link>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </Container>
          </>
        ) : (
          <Container>
            {searchlogDetail?.topSearch && (
              <>
                <h6 className="p-3 d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faFire} className="text-danger" />
                  <strong className="text-dark">Trending Searches</strong>
                </h6>

                <div className="px-3">
                  <div className="d-flex flex-wrap gap-2 ">
                    {searchlogDetail.topSearch.map((search, index) => (
                      <button
                        key={index}
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2 px-3 py-2 rounded text-truncate"
                        style={{ maxWidth: '100%' }}
                        title={search.term}
                        onClick={e => {
                          e.preventDefault();
                          setInput(search.term || '');
                          handleAddSearchLog(search.term);
                          setIsSearchOpen(false);
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                        <span
                          className="text-truncate"
                          style={{ maxWidth: '180px' }}
                        >
                          {search.term}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {searchlogDetail?.topCategories && (
              <>
                <h6 className="px-3 mt-4 mb-3 text-muted d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faFireAlt} className="text-danger" />
                  <strong>Top Categories</strong>
                </h6>

                <Row className="g-3 px-2">
                  {searchlogDetail.topCategories.map((category, index) => (
                    <Col key={index} xs={12} sm={6} md={4}>
                      <Link
                        to={`/store/${category.slug}`}
                        className="text-decoration-none"
                        onClick={e => {
                          e.preventDefault();
                          setInput(category?.name || '');
                          handleAddSearchLog(category.name);
                          setIsSearchOpen(false);
                        }}
                      >
                        <div className="d-flex align-items-center  bg-body-emphasis border rounded py-2 px-3 shadow-sm fs-9">
                          <img
                            src={
                              // category.imageUrl ||
                              'https://cdn.moglix.com/cms/element/Images_2020-07-21_14-35-04_Images_2020-04-30_03-10-15_safety@2x.png'
                            }
                            alt={category.name}
                            className="img-fluid rounded me-3"
                            style={{
                              width: '48px',
                              height: '48px',
                              objectFit: 'cover'
                            }}
                          />
                          <span className="text-muted fw-normal">
                            {category.name}
                          </span>
                        </div>
                      </Link>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {searchlogDetail?.recentSearch && (
              <>
                <h6 className="p-3 mt-3">
                  <FontAwesomeIcon icon={faClock} size="sm" />
                  <strong className="ps-2">Recent Search</strong>
                </h6>
                <div className="d-flex flex-wrap gap-2 px-2 pb-3">
                  {searchlogDetail.recentSearch.map((recent, index) => (
                    <Link
                      key={index}
                      to="#"
                      className="btn btn-outline-secondary recent-search-btn text-truncate"
                      onClick={e => {
                        e.preventDefault();
                        setInput(recent.term || '');
                        handleAddSearchLog(recent.term);
                        setIsSearchOpen(false);
                      }}
                      title={recent.term}
                    >
                      <FontAwesomeIcon icon={faClock} size="sm" />
                      <span className="text-truncate ps-1">
                        {recent.term}
                      </span>{' '}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </Container>
        )}
      </div>
    );

  const searchBox = (
    <Form
      className="position-relative"
      onSubmit={e => {
        e.preventDefault();
        handleAddSearchLog();
      }}
    >
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={input}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          className="ps-4 pe-5 py-2 rounded text-muted bg-body-emphasis w-100"
          style={{
            backgroundColor: '#fff',
            borderColor: '#e0e0e0',
            fontSize: '0.95rem'
          }}
          {...rest}
        />
        <span
          className="position-absolute bg-body-emphasis"
          style={{
            top: '50%',
            right: '10px',
            transform: 'translateY(-50%)',
            // backgroundColor: '#f1f3f5',
            borderRadius: '10%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <FontAwesomeIcon icon={faSearch} className="text-muted" />
        </span>
        {renderSearchResults()}
      </div>
    </Form>
  );

  return container ? (
    <Container fluid style={style}>
      <Row className="justify-content-center ">
        <Col xs={12} sm={10} md={8} lg={6} xl={6}>
          {searchBox}
        </Col>
      </Row>
    </Container>
  ) : (
    <div style={{ maxWidth: 800, width: '100%', ...style }} ref={wrapperRef}>
      {searchBox}
    </div>
  );
};

export default GlobalSearchBox;
